import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Ruler, ShieldCheck, Home, Droplets, UtensilsCrossed, DoorOpen, ArrowRight } from "lucide-react";

const commercialServices = [
  {
    id: "polyurethane_blockwork",
    name: "Polyurethane Blockwork",
    description: "Professional polyurethane sealing for commercial blockwork construction, ensuring weather-tight and durable joints.",
    icon: Building2,
  },
  {
    id: "render_expansion_joints",
    name: "Render Expansion Joints",
    description: "Expert sealing of render expansion joints to prevent cracking and water ingress in commercial facades.",
    icon: Ruler,
  },
  {
    id: "fire_rated_caulking",
    name: "Fire Rated Caulking & Certification",
    description: "Certified fire-rated caulking installation with full documentation and compliance certification.",
    icon: ShieldCheck,
  },
  {
    id: "internal_silicone",
    name: "Internal Silicone Works",
    description: "Complete internal silicone solutions for commercial fitouts including wet areas and general applications.",
    icon: Home,
  },
];

const residentialServices = [
  {
    id: "bathroom_caulking",
    name: "Bathroom Caulking",
    description: "Professional bathroom silicone replacement and waterproofing for showers, baths, and basins.",
    icon: Droplets,
  },
  {
    id: "kitchen_caulking",
    name: "Kitchen Caulking",
    description: "Kitchen benchtop and splashback sealing to prevent water damage and maintain hygiene.",
    icon: UtensilsCrossed,
  },
  {
    id: "structural_control_joints",
    name: "Structural Control Joints",
    description: "Professional sealing of structural control joints to manage building movement and prevent cracking.",
    icon: Ruler,
  },
];

export const ServicesSection = () => {
  return (
    <section className="py-16 lg:py-24 bg-[#F1F5F9]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#F59E0B] font-medium text-sm uppercase tracking-wider mb-4">
            What We Do
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] uppercase mb-6">
            Our Services
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-base lg:text-lg">
            From commercial high-rises to residential renovations, we deliver professional 
            caulking solutions across the Gold Coast and South East Queensland.
          </p>
        </div>

        {/* Commercial Services */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-1 bg-[#F59E0B]"></div>
            <h3 className="font-heading text-xl font-bold text-[#0F172A] uppercase">Commercial Services</h3>
            <span className="text-xs bg-[#0F172A] text-white px-3 py-1 rounded-full font-medium">Our Specialty</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commercialServices.map((service, index) => (
              <Card 
                key={service.id} 
                className="service-card bg-white border-0 shadow-sm hover:shadow-xl"
                data-testid={`service-card-${service.id}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#0F172A] rounded-xl flex items-center justify-center mb-5">
                    <service.icon className="w-7 h-7 text-[#F59E0B]" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-[#0F172A] uppercase mb-3">
                    {service.name}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Residential Services */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-1 bg-[#64748B]"></div>
            <h3 className="font-heading text-xl font-bold text-[#0F172A] uppercase">Residential Services</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {residentialServices.map((service, index) => (
              <Card 
                key={service.id} 
                className="service-card bg-white border-0 shadow-sm hover:shadow-xl"
                data-testid={`service-card-${service.id}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-5">
                    <service.icon className="w-7 h-7 text-[#64748B]" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-[#0F172A] uppercase mb-3">
                    {service.name}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/services">
            <Button
              size="lg"
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-heading font-bold uppercase tracking-wide px-8"
              data-testid="services-view-all-btn"
            >
              View All Services
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
